<template>
  <transition name="customize-fade">
    <div v-if="show" class="customize-overlay" role="dialog" :aria-label="t('dashboard.customize.title')" @click.self="$emit('close')">
      <aside class="customize-panel">
        <header class="customize-panel__header">
          <h3 class="customize-panel__title">{{ t('dashboard.customize.title') }}</h3>
          <button type="button" class="customize-panel__close" :aria-label="t('common.close')" @click="$emit('close')">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.5" fill="none" />
            </svg>
          </button>
        </header>

        <!-- ==================== 프리셋 ==================== -->
        <section class="customize-panel__section">
          <h4 class="customize-panel__section-title">{{ t('dashboard.customize.presetTitle') }}</h4>
          <div class="customize-panel__presets">
            <button
              v-for="preset in presets"
              :key="preset.id"
              type="button"
              class="customize-preset-btn"
              @click="$emit('apply-preset', preset.id)"
            >
              {{ t(preset.label) }}
            </button>
          </div>
        </section>

        <!-- ==================== 위젯 가시성 + 정렬 ==================== -->
        <section class="customize-panel__section">
          <h4 class="customize-panel__section-title">{{ t('dashboard.customize.widgetsTitle') }}</h4>
          <draggable
            :model-value="orderedItems"
            handle=".customize-row__handle"
            item-key="id"
            @update:model-value="handleReorder"
          >
            <template #item="{ element: item }">
              <div class="customize-row">
                <button type="button" class="customize-row__handle" :aria-label="t('dashboard.widget.dragHandle')">
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <circle cx="5" cy="3" r="1.2" />
                    <circle cx="5" cy="8" r="1.2" />
                    <circle cx="5" cy="13" r="1.2" />
                    <circle cx="11" cy="3" r="1.2" />
                    <circle cx="11" cy="8" r="1.2" />
                    <circle cx="11" cy="13" r="1.2" />
                  </svg>
                </button>
                <span class="customize-row__label">{{ widgetLabel(item.id) }}</span>
                <label class="customize-row__toggle">
                  <input
                    type="checkbox"
                    :checked="item.visible"
                    @change="$emit('toggle-visible', item.id)"
                  />
                  <span class="customize-row__switch" />
                </label>
              </div>
            </template>
          </draggable>
        </section>

        <footer class="customize-panel__footer">
          <button type="button" class="customize-panel__reset-btn" @click="$emit('reset')">
            {{ t('dashboard.customize.reset') }}
          </button>
          <button type="button" class="customize-panel__done-btn" @click="$emit('close')">
            {{ t('common.done') }}
          </button>
        </footer>
      </aside>
    </div>
  </transition>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed } from 'vue'
import draggable from 'vuedraggable'
import i18n from '@/locales/i18n'
import { DASHBOARD_PRESETS, WIDGET_BY_ID } from '@/constants/dashboard-widgets'

const t = i18n.global.t

const props = defineProps({
  show: { type: Boolean, default: false },
  layout: { type: Array, default: () => [] }
})

const emit = defineEmits(['close', 'apply-preset', 'reset', 'toggle-visible', 'reorder'])

const presets = DASHBOARD_PRESETS

const WIDGET_TITLE_KEY_MAP = {
  'today-hero': 'dashboard.hero.title',
  'customer-status': 'dashboard.customer.title',
  'activity-feed': 'dashboard.feed.title',
  'service-quality': 'dashboard.qualityTitle',
  'sla-compliance': 'dashboard.slaCompliance',
  'region-heatmap': 'dashboard.regionHeatmap',
  'partner-kpi': 'dashboard.partnerKpi',
  'technician-calendar': 'dashboard.calendar.title'
}

function widgetLabel(id) {
  const key = WIDGET_TITLE_KEY_MAP[id]
  return key ? t(key) : id
}

/** 정렬된 항목 — order 오름차순. 모든 위젯(visible=false 포함)을 노출하여 토글 허용 */
const orderedItems = computed(() => {
  return [...props.layout]
    .filter((item) => WIDGET_BY_ID[item.id])
    .sort((a, b) => a.order - b.order)
})

function handleReorder(next) {
  emit('reorder', next)
}
</script>

<style scoped>
.customize-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.customize-panel {
  width: 100%;
  max-width: 420px;
  height: 100%;
  background: var(--color-white, #fff);
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
}

.customize-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--color-gray-100, #E8EAED);
}

.customize-panel__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
}

.customize-panel__close {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--color-gray-500, #5C616B);
  border-radius: var(--radius-sm, 4px);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.customize-panel__close:hover {
  background: var(--color-gray-50, #F3F4F6);
  color: var(--color-primary, #3B5998);
}

.customize-panel__close svg {
  width: 14px;
  height: 14px;
}

.customize-panel__section {
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-gray-50, #F3F4F6);
}

.customize-panel__section-title {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-gray-600, #424750);
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.customize-panel__presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.customize-preset-btn {
  padding: 8px 14px;
  border-radius: var(--radius-md, 8px);
  border: 1px solid var(--color-gray-200, #B8BCC4);
  background: var(--color-white, #fff);
  color: var(--color-gray-700, #2E333B);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast, 0.15s ease);
}

.customize-preset-btn:hover {
  border-color: var(--color-primary, #3B5998);
  color: var(--color-primary, #3B5998);
  background: var(--color-primary-50, #F1F4FA);
}

.customize-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--color-gray-100, #E8EAED);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 6px;
  background: var(--color-white, #fff);
}

.customize-row__handle {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--color-gray-400, #858A93);
  cursor: grab;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.customize-row__handle:active { cursor: grabbing; }
.customize-row__handle svg { width: 14px; height: 14px; fill: currentColor; }

.customize-row__label {
  flex: 1;
  font-size: 13px;
  color: var(--color-gray-700, #2E333B);
}

.customize-row__toggle {
  position: relative;
  width: 36px;
  height: 20px;
  cursor: pointer;
  display: inline-block;
}

.customize-row__toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.customize-row__switch {
  position: absolute;
  inset: 0;
  background: var(--color-gray-200, #B8BCC4);
  border-radius: 999px;
  transition: background var(--transition-fast, 0.15s ease);
}

.customize-row__switch::before {
  content: '';
  position: absolute;
  width: 14px;
  height: 14px;
  left: 3px;
  top: 3px;
  background: var(--color-white, #fff);
  border-radius: 50%;
  transition: transform var(--transition-fast, 0.15s ease);
}

.customize-row__toggle input:checked + .customize-row__switch {
  background: var(--color-primary, #3B5998);
}

.customize-row__toggle input:checked + .customize-row__switch::before {
  transform: translateX(16px);
}

.customize-panel__footer {
  margin-top: auto;
  padding: 16px 20px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  border-top: 1px solid var(--color-gray-100, #E8EAED);
  background: var(--color-gray-50, #F8F9FA);
}

.customize-panel__reset-btn,
.customize-panel__done-btn {
  padding: 10px 18px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast, 0.15s ease);
}

.customize-panel__reset-btn {
  border: 1px solid var(--color-gray-200, #B8BCC4);
  background: var(--color-white, #fff);
  color: var(--color-gray-600, #424750);
}

.customize-panel__reset-btn:hover {
  border-color: var(--color-danger, #C0392B);
  color: var(--color-danger, #C0392B);
}

.customize-panel__done-btn {
  border: none;
  background: var(--color-primary, #3B5998);
  color: var(--color-white, #fff);
}

.customize-panel__done-btn:hover {
  background: var(--color-primary-dark, #2C4578);
}

/* ===== Mobile + Tablet (<1280px) ===== */
@media (max-width: 1279px) {
  .customize-panel {
    max-width: 100%;
  }
}

/* Transition */
.customize-fade-enter-active,
.customize-fade-leave-active {
  transition: opacity 0.2s ease;
}

.customize-fade-enter-active .customize-panel,
.customize-fade-leave-active .customize-panel {
  transition: transform 0.25s ease;
}

.customize-fade-enter-from,
.customize-fade-leave-to {
  opacity: 0;
}

.customize-fade-enter-from .customize-panel,
.customize-fade-leave-to .customize-panel {
  transform: translateX(100%);
}
</style>
